import type DialogOfferPrice from "@/components/marketplace/item/DialogOfferPrice.vue";
import type { MarketplaceItem } from "@/services/marketplace/item";
import { whenRefAvailable } from "@/utilities";
import type { Ref } from "vue";
import * as PostHog from "@/services/analytics/postHog";
import { useUser } from "@/composables/user";
import { createLoginLink } from "@/router";
import { useRoute, useRouter } from "vue-router";
import type { CollectionItemId } from "@/services/collection/item";
import type { PublicCollectionItem, PublicCollectionItem_ListItem } from "@/services/collection/item/public";

type Options = {
  dialog: Ref<InstanceType<typeof DialogOfferPrice> | null>;
} & (
  | {
      marketplaceItem: Ref<MarketplaceItem | undefined>;
    }
  | {
      collectionItem: Ref<PublicCollectionItem | PublicCollectionItem_ListItem | undefined>;
    }
);

const pendingOpenAfterLogin = ref<CollectionItemId | undefined>();

export function useOfferPriceDialog(options: Options) {
  const { isUserLoggedIn } = useUser();

  const router = useRouter();
  const route = useRoute();

  const open = () => {
    PostHog.trackEvent(PostHog.EventName.Marketplace_OfferPriceOpenDialog_Click);

    if (isUserLoggedIn.value) {
      whenRefAvailable(options.dialog).then((d) => {
        const options = assembleOpenSettings();
        d.value.open(options);
      });
    } else {
      requireLogin();
    }
  };

  const assembleOpenSettings = () => {
    if ("marketplaceItem" in options) {
      const item = options.marketplaceItem.value;
      assertItemLoaded(item);
      return {
        marketplaceItemId: item.id,
        ownerId: item.owner.id,
        initialPrice: item.price,
      };
    } else {
      const item = options.collectionItem.value;
      assertItemLoaded(item);
      return {
        collectionItemId: item.id,
        ownerId: item.collectorId,
        initialPrice: undefined,
      };
    }
  };

  const requireLogin = () => {
    if ("marketplaceItem" in options) {
      const item = options.marketplaceItem.value;
      assertItemLoaded(item);
      const { storePendingOpen } = usePendingOfferPriceDialog({ item: options.marketplaceItem });
      storePendingOpen();
    } else {
      const item = options.collectionItem.value;
      assertItemLoaded(item);
      const { storePendingOpen } = usePendingOfferPriceDialog({ item: options.collectionItem });
      storePendingOpen();
    }
    const redirect = route.fullPath;
    const loginRoute = createLoginLink(redirect);
    router.push(loginRoute);
  };

  return {
    open,
  };
}

export function usePendingOfferPriceDialog(options: { item: Ref<MarketplaceItem | PublicCollectionItem | PublicCollectionItem_ListItem | undefined> }) {
  const id = computed((): CollectionItemId | undefined => {
    const item = options.item.value;
    if (item === undefined) {
      return undefined;
    } else if ("collectionItem" in item) {
      return item.collectionItem.id;
    } else {
      return item.id;
    }
  });

  const isWaitingForLogin = computed(() => {
    return pendingOpenAfterLogin.value === id.value && id.value !== undefined;
  });

  const storePendingOpen = () => {
    pendingOpenAfterLogin.value = id.value;
  };

  const clearPendingOpen = () => {
    pendingOpenAfterLogin.value = undefined;
  };

  return {
    isWaitingForLogin,
    storePendingOpen,
    clearPendingOpen,
  };
}

function assertItemLoaded(item: MarketplaceItem | PublicCollectionItem | PublicCollectionItem_ListItem | undefined): asserts item is MarketplaceItem | PublicCollectionItem {
  if (item === undefined) {
    throw new Error("Offer price dialog: item is undefined");
  }
}
